import { Copy, Link2, Mic, Share2 } from 'lucide-react';
import { useState } from 'react';
import { formatMeetingId } from '../../shared/protocol';
import { Avatar, Button, Modal } from '../components/ui';
import { joinUrl } from '../lib/api';
import { canShareNatively, copyWithFeedback, shareLink } from '../lib/share';
import { inviteText } from '../lib/invite';
import { useStore } from '../lib/store';
import type { MeetingSession } from '../meeting/MeetingSession';
import { meetingPasscode } from './TopBar';

export function LeaveDialog({ session, onClose }: { session: MeetingSession; onClose(): void }) {
  const s = useStore(session.store);
  const [assigning, setAssigning] = useState(false);
  const others = Object.values(s.participants).filter((p) => p.id !== s.selfId && p.connected);
  const [newHost, setNewHost] = useState<string>(others[0]?.id ?? '');
  const amHost = s.participants[s.selfId!]?.role === 'host';

  if (!amHost) {
    return (
      <Modal
        title="¿Salir de la reunión?"
        onClose={onClose}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={() => session.leave()}>
              Salir de la reunión
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>Podrás volver a entrar con el mismo enlace mientras la reunión siga activa.</p>
      </Modal>
    );
  }

  if (assigning) {
    return (
      <Modal
        title="Asignar un nuevo anfitrión"
        onClose={onClose}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAssigning(false)}>
              Atrás
            </Button>
            <Button variant="danger" disabled={!newHost} onClick={() => session.leave(newHost)}>
              Asignar y salir
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>Elige quién dirigirá la reunión cuando te vayas.</p>
        <div className="host-pick" role="radiogroup" aria-label="Nuevo anfitrión">
          {others.map((p) => (
            <label key={p.id}>
              <input type="radio" name="new-host" checked={newHost === p.id} onChange={() => setNewHost(p.id)} />
              <Avatar name={p.name} seed={p.id} size={26} />
              {p.name}
            </label>
          ))}
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="¿Finalizar o salir de la reunión?" onClose={onClose}>
      <p style={{ margin: 0 }} className="muted">
        Como anfitrión puedes cerrar la reunión para todos o salir y dejar que continúe.
      </p>
      <Button variant="danger" block onClick={() => session.endForAll()}>
        Finalizar la reunión para todos
      </Button>
      <Button block onClick={() => (others.length ? setAssigning(true) : session.leave())}>
        Salir de la reunión
      </Button>
      <Button variant="ghost" block onClick={onClose}>
        Cancelar
      </Button>
    </Modal>
  );
}

export function InviteDialog({ session, onClose }: { session: MeetingSession; onClose(): void }) {
  const meeting = useStore(session.store, (s) => s.meeting)!;
  const host = useStore(session.store, (s) => Object.values(s.participants).find((p) => p.role === 'host'));
  const passcode = meetingPasscode(session);
  const link = joinUrl(meeting.id, passcode);
  const text = inviteText({ ...meeting, passcode }, host?.name);
  return (
    <Modal
      title="Invitar a otras personas"
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          Listo
        </Button>
      }
    >
      <div className="field">
        <span className="label">Enlace de la reunión</span>
        <div className="row">
          <input className="input" readOnly value={link} onFocus={(e) => e.currentTarget.select()} aria-label="Enlace de la reunión" />
          <Button onClick={() => void copyWithFeedback(link, 'enlace')}>
            <Link2 size={16} /> Copiar
          </Button>
        </div>
      </div>
      <dl className="grid-2" style={{ margin: 0 }}>
        <div>
          <dt className="label">ID de reunión</dt>
          <dd style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{formatMeetingId(meeting.id)}</dd>
        </div>
        {passcode && (
          <div>
            <dt className="label">Código de acceso</dt>
            <dd style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{passcode}</dd>
          </div>
        )}
      </dl>
      {canShareNatively() && (
        <Button variant="primary" onClick={() => void shareLink({ title: meeting.title, text: `Únete a «${meeting.title}» en Nexo Meet`, url: link })}>
          <Share2 size={16} /> Compartir enlace…
        </Button>
      )}
      <Button onClick={() => void copyWithFeedback(text, 'invitación')}>
        <Copy size={16} /> Copiar invitación completa
      </Button>
    </Modal>
  );
}

export function UnmuteRequestDialog({ session, byName }: { session: MeetingSession; byName: string }) {
  return (
    <Modal
      title="Petición para activar el audio"
      onClose={() => session.dismissUnmuteRequest()}
      footer={
        <>
          <Button variant="ghost" onClick={() => session.dismissUnmuteRequest()}>
            Seguir silenciado
          </Button>
          <Button variant="primary" onClick={() => void session.toggleAudio()}>
            <Mic size={16} /> Activar mi audio
          </Button>
        </>
      }
    >
      <p style={{ margin: 0 }}>{byName} te pide que actives tu micrófono.</p>
    </Modal>
  );
}
